import type {
  ClientAnomalyEffect,
  AnomalyChoice,
  DestinationChoice,
  FixtureChoice,
  ProposedAnomaly,
  SurveyV2,
  VocabularyEntry,
  VocabularyList,
} from '@ghp/domain'
import {
  FIXTURE_LOCATION_COMPLEMENTS,
  composeDestinationProposals,
  composeOtherDestinationProposals,
  composeTechnicalProposals,
  fixtureChoices,
  otherChoice,
  qualify,
  setIndoorFixtureCount,
  stormwaterAnomalies,
} from '@ghp/domain'
import {
  Button,
  Card,
  CardBody,
  CountInput,
  IconButton,
  InfoNote,
  Input,
  Page,
  PageBody,
  PageHeader,
  SectionLabel,
  SegmentedControl,
  Select,
  ToggleChip,
  VisuallyHidden,
} from '@ghp/ui'
import {
  ArrowLeft,
  ArrowRightCircle,
  Check,
  GitCompareArrows,
  HelpCircle,
  ListChecks,
  LogIn,
  LogOut,
  MoveRight,
  PenLine,
  TriangleAlert,
  Waves,
} from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'

import {
  FALLBACK_DESTINATIONS,
  FALLBACK_EVACUATIONS,
  FALLBACK_ORIGINS,
  FALLBACK_PROVENANCES,
  FALLBACK_TECHNICAL_ELEMENTS,
} from './AnomalyBuilder.fallback'
import s from './AnomalyBuilder.module.css'
import picker from './AnomalyPicker.module.css'
import { CheckRow } from './CheckRow'
import { NumberInput, writeOutdoorElement } from './StormwaterCard'

/**
 * **Le constructeur d'anomalie** du prototype validé (`mocks/ghp-ct/screens.js`, `anomalyPage`) :
 * une sous-page plein écran, pas une fenêtre modale. L'agent y coche ce qu'il constate, la
 * **proposition se recompose sous ses yeux**, et « Valider l'anomalie » la verse au contrôle.
 *
 * ⚠️ **La phrase composée n'est pas écrite ici** : elle vient de `@ghp/domain`
 * (`composeDestinationProposals`), parce que c'est l'archive qui part au client et que l'écran
 * jumeau du back-office porte la même grille. Cet écran coche, il ne rédige pas.
 *
 * ⚠️ **Le référentiel descendu prime, la liste du prototype est le repli** (M17, M18) : une
 * colonne qu'aucune entrée active n'alimente retombe sur les valeurs du prototype, sans code.
 * Une tablette qui n'a pas encore synchronisé laisse quand même relever une anomalie — la phrase
 * est ce qui compte, la clé arrivera par le référentiel (M21).
 */
export interface AnomalyBuilderProps {
  /** L'adresse du contrôle, sous le titre — « Contrôle #36739 · 27 Rue de la Cadaine ». */
  subtitle: ReactNode
  /** Le référentiel d'anomalies en cache (M17) — vide avant la première synchro. */
  vocabulary: VocabularyEntry[]
  /**
   * Le relevé en cours : les points d'eau comptés dans sa grille sont ce que l'anomalie désigne
   * (#312) — « WC n°2 du 1ᵉʳ étage ».
   */
  survey: SurveyV2
  /**
   * Le point d'eau que la grille signale (« Relever l'anomalie », maquette v3) : le constructeur
   * s'ouvre dessus, il ne reste qu'à dire où l'eau part.
   */
  preset?: { fixture: string; location: string }
  /**
   * Ce que le client du contrôle fait de chaque situation (#413), descendu avec la tournée :
   * recopié sur l'élément technique coché, c'est ce qui fige la règle au relevé.
   */
  anomalyEffects?: readonly ClientAnomalyEffect[]
  onCancel: () => void
  /**
   * Les anomalies composées, et **le relevé quand le constructeur l'a modifié** : un point d'eau
   * compté sur place s'écrit dans la grille, des eaux de pluie dans leur tableau — une seule donnée,
   * deux portes d'entrée.
   */
  onValidate: (anomalies: ProposedAnomaly[], survey?: SurveyV2) => void
}

export function AnomalyBuilder({
  subtitle,
  vocabulary,
  survey,
  preset,
  anomalyEffects = [],
  onCancel,
  onValidate,
}: AnomalyBuilderProps) {
  const fixtures = fixtureChoices(survey, vocabulary)
  // Une seule liste de points d'eau, celle de la grille (#347) : la colonne « Origine » les propose
  // d'abord, puis les origines qui n'en sont pas (Cuisine, Salle de bain…).
  const waterPoints = activeEntriesOf(vocabulary, 'fixture')
  const isWaterPoint = (code: string | null | undefined) =>
    waterPoints.some((entry) => entry.code === code)
  // Un point d'eau que la grille compte « Non » se choisit à son niveau : « WC » tout court doublerait
  // « WC · 1ᵉʳ étage » et se relèverait sans dire lequel.
  const designated = new Set(fixtures.map((fixture) => fixture.code))
  const origins: AnomalyChoice[] = [
    ...waterPoints
      .filter((entry) => !designated.has(entry.code))
      .map((entry) => ({ code: entry.code, label: entry.label })),
    // Une tablette pas encore synchronisée garde les anciennes origines « point d'eau » : elles ne se
    // proposent pas en double — ni par leur code, ni par leur **libellé**, puisque la case se coche
    // par lui (« Lave-linge » d'origine et le point d'eau `MAL` donnaient deux anomalies).
    ...choicesOf(vocabulary, 'origin', FALLBACK_ORIGINS).filter(
      (origin) =>
        !waterPoints.some((entry) => entry.label === origin.label) &&
        (origin.code == null || (!designated.has(origin.code) && !isWaterPoint(origin.code))),
    ),
  ]
  const provenances = choicesOf(vocabulary, 'provenance', FALLBACK_PROVENANCES)
  const evacuations = choicesOf(vocabulary, 'evacuation', FALLBACK_EVACUATIONS)
  const destinations = destinationsOf(vocabulary)
  const technicalElements = choicesOf(vocabulary, 'technical', FALLBACK_TECHNICAL_ELEMENTS)

  const [picked, setPicked] = useState<Record<BuilderColumn, readonly string[]>>({
    origin: [],
    provenance: [],
    evacuation: [],
    destination: [],
    technical: [],
  })
  const [empty, setEmpty] = useState(false)
  /** Les points d'eau cochés, et le numéro choisi pour chacun — `null` quand il est seul. */
  const [pickedFixtures, setPickedFixtures] = useState<ReadonlyMap<string, number | null>>(
    () => new Map(preset === undefined ? [] : [[`${preset.fixture}@${preset.location}`, null]]),
  )
  /**
   * Un point d'eau que la grille ne compte pas encore, **compté ici** (maquette v3) : l'étage se
   * choisit dans « Provenance », le nombre de « Non » par origine cochée. Le tout va à la grille.
   */
  const [spotLevel, setSpotLevel] = useState<string | null>(null)
  const [spotCounts, setSpotCounts] = useState<Readonly<Record<string, number>>>({})
  /** Les réponses de l'agent aux questions des règles de son client, par code de situation. */
  const [answers, setAnswers] = useState<Readonly<Record<string, boolean>>>({})
  /** Ce que l'agent écrit sous « Autre », par colonne — ponctuel, il n'entre pas au référentiel. */
  const [others, setOthers] = useState<Readonly<Record<BuilderColumn, string>>>({
    origin: '',
    provenance: '',
    evacuation: '',
    destination: '',
    technical: '',
  })
  const technicalTitleId = useId()
  /** La case du tableau des eaux pluviales déclarée ici, et ce qu'on y compte. */
  const [stormwater, setStormwater] = useState<StormwaterDraft | null>(null)

  function toggleFixture(fixture: FixtureChoice): void {
    setEmpty(false)
    setPickedFixtures((current) => {
      const next = new Map(current)
      if (next.has(keyOf(fixture))) next.delete(keyOf(fixture))
      else next.set(keyOf(fixture), null)

      return next
    })
  }

  function number(fixture: FixtureChoice, index: number | null): void {
    setPickedFixtures((current) => new Map(current).set(keyOf(fixture), index))
  }

  const designations: AnomalyChoice[] = fixtures
    .filter((fixture) => pickedFixtures.has(keyOf(fixture)))
    .map((fixture) => ({
      code: fixture.code,
      label: fixture.label,
      fixture: { ...fixture.fixture, index: pickedFixtures.get(keyOf(fixture)) ?? null },
    }))

  function toggle(column: BuilderColumn, label: string): void {
    setEmpty(false)
    if (column === 'origin' || column === 'destination') setStormwater(null)
    setPicked((current) => ({
      ...current,
      [column]: current[column].includes(label)
        ? current[column].filter((kept) => kept !== label)
        : [...current[column], label],
    }))
  }

  const pickedIn = <T extends AnomalyChoice>(column: BuilderColumn, choices: readonly T[]) =>
    choices.filter((choice) => picked[column].includes(choice.label))
  /** Une colonne dont le champ « Autre » porte du texte (M45) : écrire, c'est choisir. */
  const written = (column: BuilderColumn) => others[column].trim() !== ''
  /** Les cases cochées, puis le texte de « Autre » quand l'agent en a écrit un. */
  const chosen = (column: BuilderColumn, choices: readonly AnomalyChoice[]) => [
    ...pickedIn(column, choices),
    ...(written(column) ? [otherChoice(others[column])] : []),
  ]

  const questions = pickedIn('technical', technicalElements).flatMap(({ code }) => {
    const condition = anomalyEffects.find((effect) => effect.code === code)?.condition
    return code == null || condition == null ? [] : [{ code, condition, answer: answers[code] }]
  })

  const levels = activeEntriesOf(vocabulary, 'fixture_location')
  /**
   * Un point d'eau coché que la grille ne compte pas encore : il se compte ici. Sans les étages du
   * référentiel, la grille ne peut pas le recevoir — la provenance reste celle de toujours.
   */
  const spotted =
    levels.length === 0
      ? []
      : pickedIn('origin', origins).filter((origin) => isWaterPoint(origin.code))
  const spotDesignations: (AnomalyChoice & { count: number })[] = spotted.flatMap((origin) => {
    if (spotLevel === null || origin.code == null) return []

    return [
      {
        code: origin.code,
        label: origin.label,
        count: spotCounts[origin.label] ?? 1,
        fixture: {
          code: origin.code,
          location: spotLevel,
          where:
            FIXTURE_LOCATION_COMPLEMENTS[spotLevel] ??
            `(${labelOf(vocabulary, 'fixture_location', spotLevel) ?? spotLevel})`,
          index: null,
        },
      },
    ]
  })
  const plainOrigins = chosen('origin', origins).filter(
    (origin) => !spotDesignations.some((spot) => spot.code === origin.code),
  )
  const listed = pickedIn('destination', destinations)
  const otherHintId = useId()
  /**
   * #583 — **« Autre » est un champ toujours ouvert, en tête de colonne**, sous son titre. En fin de
   * liste, il tombait sous vingt origines, hors de l'écran ; derrière une case, il fallait un geste
   * de plus pour l'ouvrir. Un appui, on écrit : le texte écrit vaut « Autre » coché.
   */
  const otherInput = (column: BuilderColumn) => (
    <Input
      className={s.otherText}
      icon={<PenLine />}
      aria-label={OTHER_FIELD_LABELS[column]}
      aria-describedby={otherHintId}
      placeholder={`${OTHER_FIELD_LABELS[column]}…`}
      value={others[column]}
      onValueChange={(text) => {
        setEmpty(false)
        if (column === 'origin' || column === 'destination') setStormwater(null)
        setOthers((current) => ({ ...current, [column]: text }))
      }}
    />
  )
  /**
   * ⚠️ **La première cochée est retenue** : `SubmittedAnomaly` n'en porte qu'une, et la phrase de
   * « Autres » la dit.
   */
  const [provenance] = chosen('provenance', provenances)
  const [evacuation] = chosen('evacuation', evacuations)
  /**
   * ⚠️ **La provenance et l'évacuation qualifient l'anomalie** — `SubmittedAnomaly` n'en porte
   * qu'une de chaque, et le produit cartésien du prototype ne les multiplie pas. Celles écrites sous
   * « Autre » entrent dans la phrase, celles de la liste non (`qualify`, M45). Un point d'eau désigné
   * porte son niveau : la provenance le redirait (#312).
   */
  const designatedProposals = composeDestinationProposals(
    [...designations, ...spotDesignations],
    listed,
  ).map((proposal) => qualify(proposal, undefined, evacuation))
  const otherProposals = composeDestinationProposals(plainOrigins, listed).map((proposal) =>
    qualify(proposal, provenance, evacuation),
  )
  const otherDestinationProposals = written('destination')
    ? composeOtherDestinationProposals(
        [...designations, ...spotDesignations, ...plainOrigins],
        provenance,
        others.destination,
      ).map((proposal) =>
        qualify(proposal, proposal.fixture == null ? provenance : undefined, evacuation),
      )
    : []
  const proposals = [...designatedProposals, ...otherProposals, ...otherDestinationProposals]
  /**
   * L'étage d'un point d'eau est sa provenance : la colonne montre les étages de la grille — figés
   * sur celui du point d'eau désigné, à choisir pour celui qu'on compte ici.
   */
  const designatedLevels = new Set(designations.map((designation) => designation.fixture?.location))
  const showsLevels = designations.length > 0 || spotted.length > 0
  // Une origine écrite sous « Autre » a sa provenance, comme toute origine qui n'est pas un point d'eau.
  const otherOrigins = chosen('origin', origins).filter((origin) => !spotted.includes(origin))

  /** Le compteur d'une origine à compter sur place, au bout de sa ligne, une fois l'étage choisi. */
  const spotCount = (choice: AnomalyChoice) =>
    spotLevel === null || !spotted.includes(choice) ? null : (
      <CountInput
        label={`Combien de ${choice.label} non raccordés à ce niveau`}
        value={spotCounts[choice.label] ?? 1}
        onValueChange={(count) =>
          setSpotCounts((current) => ({ ...current, [choice.label]: Math.max(1, count ?? 1) }))
        }
      />
    )

  const stormwaterElements = activeEntriesOf(vocabulary, 'outdoor_element')
  const network = survey.plot?.publicNetworkType
  // Les colonnes que le réseau public laisse, comme le tableau : EU en séparatif, unitaire en
  // unitaire, les deux s'il n'est pas renseigné.
  const stormwaterColumns =
    network === 'separate'
      ? ['RESEAU_EU']
      : network === 'combined'
        ? ['RESEAU_UNITAIRE']
        : ['RESEAU_EU', 'RESEAU_UNITAIRE']
  const stormwaterSurvey =
    stormwater === null
      ? null
      : writeOutdoorElement(
          survey,
          { element: stormwater.element, destination: stormwater.destination },
          { count: stormwater.count, areaM2: stormwater.areaM2, areaNote: stormwater.areaNote },
          anomalyEffects,
        )
  const stormwaterDeclared =
    stormwater !== null &&
    (stormwater.count !== null || stormwater.areaM2 !== null || stormwater.areaNote !== null)
  // La phrase du tableau, effet du client compris : en unitaire « conforme », il n'y en a pas.
  const stormwaterSentence =
    stormwaterDeclared && stormwaterSurvey !== null
      ? stormwaterAnomalies(stormwaterSurvey, vocabulary).find(
          (anomaly) => anomaly.key === `${stormwater.element}/${stormwater.destination}`,
        )?.sentence
      : undefined

  function pickStormwater(element: string): void {
    setEmpty(false)
    if (stormwater?.element === element) {
      setStormwater(null)
      return
    }
    // Les eaux de pluie partent toujours aux EU ou à l'unitaire ici : les autres origines n'ont plus de
    // sens, cochées ou écrites sous « Autre ».
    setPicked((current) => ({ ...current, origin: [], destination: [] }))
    setOthers((current) => ({ ...current, origin: '', destination: '' }))
    setPickedFixtures(new Map())
    const destination = stormwaterColumns[0] ?? 'RESEAU_EU'
    const kept = survey.outdoorElements?.find(
      (cell) => cell.element === element && cell.destination === destination,
    )
    setStormwater({
      element,
      destination,
      count: kept?.count ?? 1,
      areaM2: kept?.areaM2 ?? null,
      areaNote: kept?.areaNote ?? null,
    })
  }

  function validate(): void {
    const technical = composeTechnicalProposals(
      chosen('technical', technicalElements),
      anomalyEffects,
      answers,
    )

    if (proposals.length === 0 && technical.length === 0 && !stormwaterDeclared) {
      setEmpty(true)
      return
    }

    // Ce que le constructeur écrit au relevé : le tableau des eaux pluviales, la grille des points
    // d'eau comptés sur place — jamais une seconde copie.
    let next: SurveyV2 | undefined =
      stormwaterDeclared && stormwaterSurvey !== null ? stormwaterSurvey : undefined
    for (const spot of spotDesignations) {
      if (spot.fixture == null) continue
      const base = next ?? survey
      const counted =
        base.indoorFixtures?.find(
          (cell) =>
            cell.fixture === spot.fixture?.code &&
            cell.location === spot.fixture.location &&
            !cell.connected,
        )?.count ?? 0
      next = {
        ...base,
        indoorFixtures: setIndoorFixtureCount(
          base.indoorFixtures,
          { fixture: spot.fixture.code, location: spot.fixture.location, connected: false },
          Math.max(counted, spot.count),
        ),
      }
    }

    const anomalies = [...proposals, ...technical]

    if (next === undefined) onValidate(anomalies)
    else onValidate(anomalies, next)
  }

  return (
    <Page>
      <PageHeader
        back={
          <IconButton label="Revenir au contrôle" onClick={onCancel}>
            <ArrowLeft size={20} aria-hidden />
          </IconButton>
        }
        title="Nouvelle anomalie"
        subtitle={subtitle}
      >
        <Button tone="neutral" onClick={onCancel}>
          Annuler
        </Button>
        <Button tone="brand" onClick={validate}>
          <Check size={18} aria-hidden />
          Valider l'anomalie
        </Button>
      </PageHeader>

      <PageBody className={s.body}>
        <div className={s.columns}>
          <ChoiceColumn
            title="Origine"
            icon={<LogIn size={15} aria-hidden />}
            choices={origins}
            picked={picked.origin}
            onToggle={(label) => toggle('origin', label)}
            other={otherInput('origin')}
            trailing={spotCount}
            after={
              <StormwaterChoices
                elements={stormwaterElements}
                columns={stormwaterColumns}
                vocabulary={vocabulary}
                draft={stormwater}
                onPick={pickStormwater}
                onChange={setStormwater}
              />
            }
          >
            {fixtures.length > 0 && (
              <FixtureChoices
                fixtures={fixtures}
                picked={pickedFixtures}
                onToggle={(fixture) => {
                  setStormwater(null)
                  toggleFixture(fixture)
                }}
                onNumber={number}
              />
            )}
          </ChoiceColumn>
          <div className={s.middle}>
            {showsLevels && (
              <ChoiceColumn
                title="Provenance"
                icon={<MoveRight size={15} aria-hidden />}
                choices={levels.map((level) => ({ code: level.code, label: level.label }))}
                picked={levels
                  .filter((level) => designatedLevels.has(level.code) || level.code === spotLevel)
                  .map((level) => level.label)}
                disabled={spotted.length === 0}
                onToggle={(label) => {
                  const level = levels.find((candidate) => candidate.label === label)?.code ?? null
                  setSpotLevel((current) => (current === level ? null : level))
                }}
                after={
                  spotted.length === 0 ? (
                    <InfoNote>L’étage est celui du point d’eau choisi.</InfoNote>
                  ) : spotLevel === null ? (
                    <InfoNote>Choisissez l’étage : le relevé comptera le point d’eau.</InfoNote>
                  ) : null
                }
              />
            )}
            {(!showsLevels || otherOrigins.length > 0) && (
              <ChoiceColumn
                title={showsLevels ? 'Provenance des autres origines' : 'Provenance'}
                icon={<MoveRight size={15} aria-hidden />}
                choices={provenances}
                picked={picked.provenance}
                onToggle={(label) => toggle('provenance', label)}
                other={otherInput('provenance')}
              />
            )}
            <ChoiceColumn
              title="Évacuation"
              icon={<Waves size={15} aria-hidden />}
              choices={evacuations}
              picked={picked.evacuation}
              onToggle={(label) => toggle('evacuation', label)}
              other={otherInput('evacuation')}
            />
          </div>
          {stormwater !== null ? (
            <Card>
              <CardBody className={s.column}>
                <SectionLabel className={s.sectionLabel}>
                  <LogOut size={15} aria-hidden />
                  Destination constatée
                </SectionLabel>
                <InfoNote>
                  {stormwater.destination === 'RESEAU_UNITAIRE'
                    ? 'Vers le réseau unitaire : la règle « collecte non séparative » du client dit si c’est une anomalie.'
                    : 'Vers le réseau d’eaux usées : de l’eau de pluie qui n’a rien à y faire — c’est ce qui fait l’anomalie.'}
                </InfoNote>
              </CardBody>
            </Card>
          ) : (
            <ChoiceColumn
              title="Destination constatée"
              icon={<LogOut size={15} aria-hidden />}
              choices={destinations}
              picked={picked.destination}
              severe
              onToggle={(label) => toggle('destination', label)}
              other={otherInput('destination')}
            />
          )}
        </div>

        <VisuallyHidden id={otherHintId}>{OTHER_HINT}</VisuallyHidden>

        <Card>
          <CardBody>
            <SectionLabel className={s.sectionLabel}>
              <GitCompareArrows size={16} aria-hidden />
              Proposition (générée automatiquement)
            </SectionLabel>
            {/* `role="status"` : la phrase se recompose à chaque coche, et l'agent qui vient de
                cocher doit l'entendre changer sans quitter la colonne. */}
            <div className={s.proposition} role="status">
              {stormwaterSentence !== undefined ? (
                <p className={s.propositionLine} data-severity="bad">
                  <ArrowRightCircle size={15} aria-hidden />
                  {stormwaterSentence}
                </p>
              ) : proposals.length === 0 ? (
                <span className={s.propositionEmpty}>
                  Sélectionnez une origine et une destination…
                </span>
              ) : (
                proposals.map((proposal) => (
                  <p
                    key={proposal.sentence}
                    className={s.propositionLine}
                    data-severity={proposal.severity}
                  >
                    {proposal.severity === 'warn' ? (
                      <HelpCircle size={15} aria-hidden />
                    ) : (
                      <ArrowRightCircle size={15} aria-hidden />
                    )}
                    {proposal.sentence}
                  </p>
                ))
              )}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <SectionLabel id={technicalTitleId} className={s.sectionLabel}>
              <ListChecks size={16} aria-hidden />
              Éléments techniques relevés
            </SectionLabel>
            <InfoNote icon={<TriangleAlert size={14} />}>
              Cocher « Non raccordé » ou « REFUS » (accès refusé) bascule la conclusion en{' '}
              <b>non raccordé</b>.
            </InfoNote>
            {otherInput('technical')}
            <div className={s.technicalGrid} role="group" aria-labelledby={technicalTitleId}>
              {technicalElements.map((element) => (
                <CheckRow
                  key={element.label}
                  checked={picked.technical.includes(element.label)}
                  onCheckedChange={() => toggle('technical', element.label)}
                >
                  {element.label}
                </CheckRow>
              ))}
            </div>
            {/* La question d'une règle du client qui dépend de ce que l'agent voit (#413) : sous
                la grille, la rangée de la frame 1.5e — la question, puis Oui / Non au bout. Sans
                réponse, la situation compte comme une anomalie, et l'écran le dit (M21). */}
            {questions.map(({ code, condition, answer }) => (
              <div key={code} className={picker.head}>
                {/* Le groupe porte déjà la question comme nom accessible. */}
                <span aria-hidden>{condition}</span>
                <SegmentedControl
                  label={condition}
                  options={[
                    { value: 'yes', label: 'Oui' },
                    { value: 'no', label: 'Non' },
                  ]}
                  value={answer === undefined ? '' : answer ? 'yes' : 'no'}
                  onValueChange={(value) =>
                    setAnswers((current) => ({ ...current, [code]: value === 'yes' }))
                  }
                />
              </div>
            ))}
            {questions.some(({ answer }) => answer === undefined) && (
              <InfoNote icon={<TriangleAlert size={14} />}>
                Sans réponse, la situation comptera comme une anomalie.
              </InfoNote>
            )}
          </CardBody>
        </Card>

        {/* Le refus du prototype, dit à l'écran plutôt qu'en message fugace : une anomalie vide
            n'a rien à verser au contrôle, et l'agent doit savoir pourquoi le bouton n'a rien fait. */}
        {empty && (
          <InfoNote tone="warn" icon={<TriangleAlert size={18} />} role="alert">
            Sélectionnez une origine et une destination, ou un élément technique.
          </InfoNote>
        )}
      </PageBody>
    </Page>
  )
}

/** Les cinq colonnes du constructeur — les listes de vocabulaire qu'il consomme (M17). */
type BuilderColumn = Extract<
  VocabularyList,
  'origin' | 'provenance' | 'evacuation' | 'destination' | 'technical'
>

/** Le nom de chaque champ « Autre », qui en est aussi le texte d'attente. */
const OTHER_FIELD_LABELS: Record<BuilderColumn, string> = {
  origin: 'Autre origine',
  provenance: 'Autre provenance',
  evacuation: 'Autre évacuation',
  destination: 'Autre destination',
  technical: 'Autre élément technique',
}

const OTHER_HINT =
  'Ponctuel : vaut pour ce contrôle, n’entre ni dans le référentiel ni dans les règles.'

/** Les entrées actives d'une liste, dans l'ordre du back-office — le repli quand il n'y en a pas. */
function choicesOf(
  vocabulary: readonly VocabularyEntry[],
  list: BuilderColumn,
  fallback: readonly AnomalyChoice[],
): readonly AnomalyChoice[] {
  const entries = activeEntriesOf(vocabulary, list)

  return entries.length === 0
    ? fallback
    : entries.map((entry) => ({ code: entry.code, label: entry.label }))
}

/**
 * Les destinations, **gravité comprise** : c'est elle qui décide de la phrase composée.
 * Une entrée du référentiel qui n'en porte pas vaut `warn` — la même lecture qu'`AnomalyPicker` :
 * une destination sans gravité mérite quand même un œil, jamais rien.
 */
function destinationsOf(vocabulary: readonly VocabularyEntry[]): readonly DestinationChoice[] {
  const entries = activeEntriesOf(vocabulary, 'destination')

  return entries.length === 0
    ? FALLBACK_DESTINATIONS
    : entries.map((entry) => ({
        code: entry.code,
        label: entry.label,
        severity: entry.severity ?? 'warn',
      }))
}

function activeEntriesOf(
  vocabulary: readonly VocabularyEntry[],
  list: VocabularyList,
): VocabularyEntry[] {
  return vocabulary
    .filter((entry) => entry.list === list && entry.active)
    .sort((left, right) => left.sortOrder - right.sortOrder)
}

/** La clé d'un point d'eau de la grille : son code et son niveau. */
const keyOf = (fixture: FixtureChoice) => `${fixture.fixture.code}@${fixture.fixture.location}`

/**
 * #312 — les points d'eau comptés « Non » au relevé (#415), en tête de la colonne « Origine ». Un
 * point d'eau compté plusieurs fois « Non » à son niveau fait choisir **lequel** : c'est ce qui dit à l'usager quoi
 * faire réparer. Le choix se fait **sur la ligne** du point d'eau (frame Terrain 1.5e).
 */
function FixtureChoices({
  fixtures,
  picked,
  onToggle,
  onNumber,
}: {
  fixtures: readonly FixtureChoice[]
  picked: ReadonlyMap<string, number | null>
  onToggle: (fixture: FixtureChoice) => void
  onNumber: (fixture: FixtureChoice, index: number | null) => void
}) {
  return (
    <div className={s.fixtures}>
      {fixtures.map((fixture) => {
        const chosen = picked.has(keyOf(fixture))
        const where = fixture.fixture.where.replace(/^\(|\)$/gu, '')

        return (
          <div key={keyOf(fixture)} className={picker.head}>
            <CheckRow checked={chosen} onCheckedChange={() => onToggle(fixture)}>
              {fixture.label} · {labelOfWhere(where)} ({String(fixture.count)})
            </CheckRow>
            {chosen && fixture.count > 1 && (
              <Select
                width="auto"
                aria-label={`Lequel des ${String(fixture.count)} — ${fixture.label}`}
                options={[
                  ...Array.from({ length: fixture.count }, (_, at) => ({
                    value: String(at + 1),
                    label: `n°${String(at + 1)}`,
                  })),
                  { value: 'unset', label: 'Non précisé' },
                ]}
                value={String(picked.get(keyOf(fixture)) ?? 'unset')}
                onValueChange={(value) =>
                  onNumber(fixture, value === 'unset' || value === null ? null : Number(value))
                }
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Le niveau tel que la case le nomme : « du 1ᵉʳ étage » se lit « 1ᵉʳ étage » à côté du point d'eau,
 * « de la dépendance » se lit « dépendance ».
 */
function labelOfWhere(where: string): string {
  return where.replace(/^(du |de la |de l’|de l')/u, '')
}

function ChoiceColumn({
  title,
  icon,
  choices,
  picked,
  severe,
  disabled,
  onToggle,
  children,
  after,
  trailing,
  other,
}: {
  title: string
  icon: ReactNode
  choices: readonly AnomalyChoice[]
  picked: readonly string[]
  /** La destination constatée : cochée, elle passe au rouge de l'état « non conforme ». */
  severe?: boolean
  /** Une colonne qui montre sans se laisser cocher : l'étage d'un point d'eau désigné. */
  disabled?: boolean
  onToggle: (label: string) => void
  /** Ce qui précède les cases de la colonne — les points d'eau de la grille, pour l'origine. */
  children?: ReactNode
  /** Ce qui les suit — les eaux de pluie, pour l'origine. */
  after?: ReactNode
  /** Le champ « Autre » de la colonne, sous son titre (#583). */
  other?: ReactNode
  /** Ce qui se pose au bout de la ligne d'une case — le compteur sur place d'une origine (1.5e). */
  trailing?: (choice: AnomalyChoice) => ReactNode
}) {
  const titleId = useId()

  return (
    <Card>
      <CardBody className={s.column}>
        <SectionLabel id={titleId} className={s.sectionLabel}>
          {icon}
          {title}
        </SectionLabel>
        {other}
        {children}
        {/* Un groupe nommé par son intertitre : sans lui, un lecteur d'écran annonce quinze
            cases à cocher d'affilée sans jamais dire que celles-ci sont des origines. */}
        <div className={s.choices} role="group" aria-labelledby={titleId}>
          {choices.map((choice) => {
            const row = (
              <CheckRow
                key={choice.label}
                checked={picked.includes(choice.label)}
                severe={severe}
                disabled={disabled}
                onCheckedChange={() => onToggle(choice.label)}
              >
                {choice.label}
              </CheckRow>
            )
            const extra = trailing?.(choice) ?? null

            return extra === null ? (
              row
            ) : (
              <div key={choice.label} className={picker.head}>
                {row}
                {extra}
              </div>
            )
          })}
        </div>
        {after}
      </CardBody>
    </Card>
  )
}

function labelOf(
  vocabulary: readonly VocabularyEntry[],
  list: VocabularyList,
  code: string,
): string | undefined {
  return vocabulary.find((entry) => entry.list === list && entry.code === code)?.label
}

interface StormwaterDraft {
  element: string
  destination: string
  count: number | null
  areaM2: number | null
  areaNote: string | null
}

/**
 * Les eaux de pluie depuis le constructeur : l'élément, sa colonne — « Réseau EU » ou « Réseau
 * unitaire » —, le nombre et la surface, écrits dans le tableau du Relevé, qui en reste la seule
 * donnée.
 */
function StormwaterChoices({
  elements,
  columns,
  vocabulary,
  draft,
  onPick,
  onChange,
}: {
  elements: readonly VocabularyEntry[]
  columns: readonly string[]
  vocabulary: readonly VocabularyEntry[]
  draft: StormwaterDraft | null
  onPick: (element: string) => void
  onChange: (draft: StormwaterDraft) => void
}) {
  const titleId = useId()
  const element = elements.find((candidate) => candidate.code === draft?.element)
  const columnLabel = (code: string) => labelOf(vocabulary, 'outdoor_destination', code) ?? code

  return (
    <div className={s.inline}>
      <SectionLabel id={titleId} className={s.sectionLabel}>
        <Waves size={15} aria-hidden />
        Eaux pluviales
      </SectionLabel>
      <div className={s.choices} role="group" aria-labelledby={titleId}>
        {elements.map((candidate) => (
          <CheckRow
            key={candidate.code}
            checked={draft?.element === candidate.code}
            onCheckedChange={() => onPick(candidate.code)}
          >
            {candidate.label}
          </CheckRow>
        ))}
      </div>
      {element !== undefined && draft !== null && (
        <div className={s.inline}>
          {columns.length > 1 && (
            <div className={s.choices}>
              {columns.map((column) => (
                <ToggleChip
                  key={column}
                  pressed={draft.destination === column}
                  onPressedChange={() => onChange({ ...draft, destination: column })}
                >
                  {columnLabel(column)}
                </ToggleChip>
              ))}
            </div>
          )}
          <CountInput
            label={`Nombre — ${element.label}`}
            value={draft.count}
            onValueChange={(count) => onChange({ ...draft, count })}
          />
          <div className={s.measure}>
            {/* Le champ du tableau, à sa largeur : pleine colonne, il poussait PM et ? hors champ. */}
            <NumberInput
              key={draft.areaNote ?? 'measure'}
              className={s.areaField}
              unit="m²"
              aria-label={`Surface « ${element.label} » en m²`}
              placeholder={
                draft.areaNote === 'memo' ? 'PM' : draft.areaNote === 'unknown' ? '?' : '—'
              }
              defaultValue={draft.areaNote === null ? draft.areaM2 : null}
              disabled={draft.areaNote !== null}
              onValueChange={(areaM2) => onChange({ ...draft, areaM2 })}
            />
            {(['memo', 'unknown'] as const).map((note) => (
              <ToggleChip
                key={note}
                pressed={draft.areaNote === note}
                aria-label={`Surface « ${element.label} » ${note === 'memo' ? 'pour mémoire' : 'inconnue'}`}
                onPressedChange={() =>
                  onChange({
                    ...draft,
                    areaM2: null,
                    areaNote: draft.areaNote === note ? null : note,
                  })
                }
              >
                {note === 'memo' ? 'PM' : '?'}
              </ToggleChip>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
